import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import poxo from "../img/contes_presi.jpg";
import adri from "../img/manco_presi.jpeg";
import albe from "../img/albe_presi.jpg";
import alber from "../img/alberic.png";
import contestano from "../img/contestano.png";
import manco from "../img/manco.png";
import "./PlayerReview.css";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Review {
  img: string;
  shield: string;
  text: string;
  author: string;
  rol: string;
  rating: number;
}

export const PlayerReview = () => {
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(0);
  const [animatedElements, setAnimatedElements] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const avatarsRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Memoized reviews data
  const reviews = useMemo<Review[]>(() => [
    {
      img: poxo,
      shield: contestano,
      text: t("home.reviews.review_1.description"),
      author: "Gustavo Baena",
      rol: "President",
      rating: 5,
    },
    {
      img: adri,
      shield: manco,
      author: "Julio Peris",
      text: t("home.reviews.review_2.description"),
      rol: "President",
      rating: 5,
    },
    {
      img: albe,
      shield: alber,
      author: "Alfonso Cabrera",
      text: t("home.reviews.review_3.description"),
      rol: "President",
      rating: 5,
    },
  ], [t]);

  const selectedReview = reviews[selectedReviewIndex];

  // Intersection Observer setup
  useEffect(() => {
    const elementsToObserve = [
      { ref: headerRef, id: "header" },
      { ref: avatarsRef, id: "avatars" },
      { ref: cardRef, id: "card" },
      { ref: progressRef, id: "progress" }
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.getAttribute("data-animate");
            if (elementId) {
              setAnimatedElements(prev => new Set([...prev, elementId]));
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    elementsToObserve.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      elementsToObserve.forEach(({ ref }) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
  }, []);

  const handlePrevious = useCallback(() => {
    setSelectedReviewIndex(prev =>
      prev > 0 ? prev - 1 : reviews.length - 1
    );
  }, [reviews.length]);

  const handleNext = useCallback(() => {
    setSelectedReviewIndex(prev =>
      prev < reviews.length - 1 ? prev + 1 : 0
    );
  }, [reviews.length]);

  const handleReviewSelect = useCallback((index: number) => {
    setSelectedReviewIndex(index);
  }, []);

  const getAnimationClass = useCallback((elementId: string, baseClass = "") => {
    const isAnimated = animatedElements.has(elementId);
    return `${baseClass} transition-all duration-1000 ease-out ${isAnimated
      ? "opacity-100 translate-y-0 translate-x-0 scale-100"
      : "opacity-0 translate-y-8 scale-95"
      }`;
  }, [animatedElements]);

  return (
    <div
      ref={sectionRef}
      className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-20 px-4 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <HeaderSection
          //@ts-ignore
          headerRef={headerRef}
          animatedElements={animatedElements}
          getAnimationClass={getAnimationClass}
        />

        {/* Avatar Selection - Desktop */}
        <AvatarSelector
          reviews={reviews}
          selectedReviewIndex={selectedReviewIndex}
          //@ts-ignore
          avatarsRef={avatarsRef}
          animatedElements={animatedElements}
          getAnimationClass={getAnimationClass}
          onReviewSelect={handleReviewSelect}
        />

        {/* Main Review Card */}
        <ReviewCard
          selectedReview={selectedReview}
          //@ts-ignore
          cardRef={cardRef}
          animatedElements={animatedElements}
          getAnimationClass={getAnimationClass}
        />

        {/* Progress Indicators */}
        {/* <ProgressIndicators
          reviews={reviews}
          selectedReviewIndex={selectedReviewIndex}
          //@ts-ignore
          progressRef={progressRef}
          animatedElements={animatedElements}
          getAnimationClass={getAnimationClass}
          onReviewSelect={handleReviewSelect}
        /> */}
      </div>
    </div>
  );
};

// Extracted Header Component
const HeaderSection: React.FC<{
  headerRef: React.RefObject<HTMLDivElement>;
  animatedElements: Set<string>;
  getAnimationClass: (elementId: string, baseClass?: string) => string;
}> = ({ headerRef, animatedElements, getAnimationClass }) => {
  const { t } = useTranslation();

  return (
    <div
      ref={headerRef}
      data-animate="header"
      className={getAnimationClass("header", "text-center mb-16")}
    >
      <h2 className="text-5xl md:text-6xl font-bold text-slate-800 mb-4 transform transition-all duration-1000 delay-200">
        {t("home.reviews.title")}
      </h2>
      <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full transform transition-all duration-1000 delay-400"></div>
    </div>
  );
};

// Extracted Avatar Selector Component
const AvatarSelector: React.FC<{
  reviews: Review[];
  selectedReviewIndex: number;
  avatarsRef: React.RefObject<HTMLDivElement>;
  animatedElements: Set<string>;
  getAnimationClass: (elementId: string, baseClass?: string) => string;
  onReviewSelect: (index: number) => void;
}> = ({
  reviews,
  selectedReviewIndex,
  avatarsRef,
  animatedElements,
  getAnimationClass,
  onReviewSelect
}) => {
    return (
      <div
        ref={avatarsRef}
        data-animate="avatars"
        className={getAnimationClass(
          "avatars",
          "hidden lg:flex justify-center items-center gap-8 mb-16"
        )}
      >
        {reviews.map((review, index) => {
          const isSelected = selectedReviewIndex === index;

          return (
            <button
              key={`${review.author}-${index}`}
              type="button"
              onClick={() => onReviewSelect(index)}
              className={`relative focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 rounded-full transition-all duration-500 transform hover:scale-110 ${isSelected ? "scale-110 z-10" : "scale-90 opacity-60 hover:opacity-80"
                }`}
              aria-label={`Select review from ${review.author}`}
              style={{ animationDelay: `${index * 100 + 600}ms` }}
            >
              <div
                className={`w-20 h-20 md:w-24 md:h-24 rounded-full border-4 transition-all duration-300 ${isSelected
                  ? "border-blue-500 shadow-xl shadow-blue-200"
                  : "border-white shadow-lg"
                  } ${animatedElements.has("avatars") ? "animate-bounce-gentle" : ""}`}
                style={{
                  animationDelay: `${index * 150 + 800}ms`,
                  animationDuration: "2s",
                  animationIterationCount: "1",
                }}
              >
                <img
                  src={review.img}
                  alt={review.author}
                  className="w-full h-full rounded-full object-cover"
                  loading="lazy"
                  width={96}
                  height={96}
                />
              </div>
              {isSelected && (
                <div
                  className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-blue-500 rounded-full animate-pulse"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>
    );
  };

// Extracted Review Card Component
const ReviewCard: React.FC<{
  selectedReview: Review;
  cardRef: React.RefObject<HTMLDivElement>;
  animatedElements: Set<string>;
  getAnimationClass: (elementId: string, baseClass?: string) => string;
}> = ({ selectedReview, cardRef, animatedElements, getAnimationClass }) => {
  const cardAnimated = animatedElements.has("card");

  return (
    <div className="relative max-w-4xl mx-auto">
      <div
        ref={cardRef}
        data-animate="card"
        className={getAnimationClass(
          "card",
          "bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 md:p-12 relative overflow-hidden"
        )}
      >
        {/* Background decorations */}
        <div
          className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full opacity-30 transform translate-x-32 -translate-y-32 transition-all duration-2000 ease-out"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-100 to-pink-100 rounded-full opacity-20 transform -translate-x-24 translate-y-24 transition-all duration-2000 ease-out"
          aria-hidden="true"
        />

        <div className="relative z-10">
          {/* Team Shield */}
          <div className="flex flex-col items-center mb-8 gap-2">
            <div className="w-16 h-16 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center shadow-md">
              <img
                src={selectedReview.shield}
                alt={`${selectedReview.author}'s team shield`}
                className="w-12 h-12 object-contain rounded-full"
                loading="lazy"
                width={48}
                height={48}
              />
            </div>
          </div>

          {/* Quote */}
          <div
            className={`text-center mb-8 transition-all duration-1000 ${cardAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            style={{ transitionDelay: "900ms" }}
          >
            <blockquote className="text-lg md:text-xl text-slate-700 leading-relaxed font-medium italic max-w-3xl mx-auto">
              {selectedReview.text}
            </blockquote>
          </div>

          {/* Rating Stars */}
          <div
            className={`flex justify-center mb-6 transition-all duration-1000 ${cardAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            style={{ transitionDelay: "1000ms" }}
            aria-label={`Rating: ${selectedReview.rating} out of 5 stars`}
          >
            {Array.from({ length: selectedReview.rating }).map((_, i) => (
              <Star
                key={i}
                className={`w-6 h-6 text-yellow-400 fill-current transition-all duration-300 ${cardAnimated ? "scale-100" : "scale-0"
                  }`}
                style={{
                  transitionDelay: `${1100 + i * 100}ms`,
                }}
                aria-hidden="true"
              />
            ))}
          </div>

          {/* Author Info */}
          <div
            className={`text-center transition-all duration-1000 ${cardAnimated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            style={{ transitionDelay: "1100ms" }}
          >
            <h4 className="text-xl font-bold text-slate-800 mb-1">
              {selectedReview.author}
            </h4>
            <p className="text-blue-600 font-medium">
              {selectedReview.rol}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Extracted Progress Indicators Component
const ProgressIndicators: React.FC<{
  reviews: Review[];
  selectedReviewIndex: number;
  progressRef: React.RefObject<HTMLDivElement>;
  animatedElements: Set<string>;
  getAnimationClass: (elementId: string, baseClass?: string) => string;
  onReviewSelect: (index: number) => void;
}> = ({
  reviews,
  selectedReviewIndex,
  progressRef,
  animatedElements,
  getAnimationClass,
  onReviewSelect
}) => {
    const progressAnimated = animatedElements.has("progress");

    return (
      <div
        ref={progressRef}
        data-animate="progress"
        className={getAnimationClass("progress", "flex justify-center mt-8 gap-2")}
        style={{ transitionDelay: "1300ms" }}
      >
        {reviews.map((_, index) => {
          const isSelected = selectedReviewIndex === index;

          return (
            <button
              key={index}
              type="button"
              onClick={() => onReviewSelect(index)}
              className={`w-3 h-3 rounded-full transition-all duration-500 focus:outline-none focus:ring-2 focus:ring-blue-300 ${isSelected
                ? "bg-blue-500 w-8"
                : "bg-slate-300 hover:bg-slate-400"
                } ${progressAnimated ? "scale-100" : "scale-0"}`}
              style={{
                transitionDelay: progressAnimated ? "0ms" : `${1300 + index * 50}ms`,
              }}
              aria-label={`Go to review ${index + 1} of ${reviews.length}`}
              aria-current={isSelected ? "true" : "false"}
            />
          );
        })}
      </div>
    );
  };
